import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  financeService,
  type FinanceAssignee,
  type FinanceProjectRow,
  type FinanceRequestRow,
  type FinanceSummary,
} from '@/services/financeService'
import { useAuthStore } from '@/stores/authStore'
import { financeRequestDetailPath } from '@/utils/financeRequestPaths'

export type FinanceiroTab = 'dashboard' | 'chamados' | 'projetos' | 'pagamentos'
export type ProjectFilter = 'todos' | 'novos_30d' | 'sem_contrato' | 'ativos'
export type QueueSlaFilter = '' | 'overdue' | 'due_soon' | 'ok'
export type QueueLifecycleFilter = 'ativos' | 'pausados' | 'cancelados' | 'historico'
export type FinanceStatTarget =
  | 'open_requests'
  | 'sla_due_soon'
  | 'sla_overdue'
  | 'projects_no_contract'

const STATUS_LABELS: Record<string, string> = {
  NOVO: 'Novo',
  EM_ANALISE: 'Em análise',
  EM_ANDAMENTO: 'Em andamento',
  AGUARDANDO_SOLICITANTE: 'Aguardando solicitante',
  RESPONDIDO_SOLICITANTE: 'Respondido pelo solicitante',
  AJUSTAR_PROPOSTA: 'Ajustar proposta',
  PROPOSTA_AJUSTADA: 'Proposta ajustada',
  AGUARDANDO_APROVACAO: 'Aguardando aprovação',
  CONTRATO_GERADO_PDF: 'Contrato gerado em PDF',
  CONTRATO_ENVIADO: 'Contrato enviado',
  CONTRATO_ASSINADO: 'Contrato assinado',
  CONCLUIDO: 'Concluído',
  CANCELADO: 'Cancelado',
  PAUSADO: 'Pausado',
  AGUARDANDO_CONTRATO: 'Aguardando contrato',
  EM_IMPLANTACAO: 'Em implantação',
  ATIVO: 'Ativo',
  ENCERRADO: 'Encerrado',
}

export function formatFinanceStatus(status: string) {
  return STATUS_LABELS[status] ?? status
}

const QUEUE_ROLES = ['admin', 'financeiro', 'manager']

export function useFinanceiroPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const userRole = (useAuthStore((s) => s.user?.role) ?? '').toLowerCase()
  const canManageQueue = QUEUE_ROLES.includes(userRole)

  const rawTab = searchParams.get('tab')
  const tab: FinanceiroTab =
    rawTab === 'fila' || rawTab === 'chamados'
      ? 'chamados'
      : rawTab === 'dashboard' ||
          rawTab === 'projetos' ||
          rawTab === 'pagamentos'
        ? rawTab
        : 'dashboard'
  const legacyRequestId = searchParams.get('request')
  const selectedProjectId = searchParams.get('project')

  const [summary, setSummary] = useState<FinanceSummary | null>(null)
  const [requests, setRequests] = useState<FinanceRequestRow[]>([])
  const [projects, setProjects] = useState<FinanceProjectRow[]>([])
  const [loading, setLoading] = useState(true)
  const [projectFilter, setProjectFilter] = useState<ProjectFilter>('todos')
  const [queueLifecycle, setQueueLifecycle] = useState<QueueLifecycleFilter>('ativos')
  const [queueStatus, setQueueStatus] = useState('')
  const [queueType, setQueueType] = useState('')
  const [queueSla, setQueueSla] = useState<QueueSlaFilter>('')
  const [queueAssignee, setQueueAssignee] = useState('')
  const [queueUnassigned, setQueueUnassigned] = useState(false)
  const [queueSearch, setQueueSearch] = useState('')
  const [queueArea, setQueueArea] = useState('')
  const [assignees, setAssignees] = useState<FinanceAssignee[]>([])
  const [typeOptions, setTypeOptions] = useState<{ key: string; label: string }[]>([])
  const [error, setError] = useState<string | null>(null)

  const setTab = useCallback(
    (next: FinanceiroTab) => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev)
        p.set('tab', next)
        p.delete('request')
        return p
      })
    },
    [setSearchParams],
  )

  const goToStatView = useCallback(
    (target: FinanceStatTarget) => {
      if (target === 'projects_no_contract') {
        setProjectFilter('sem_contrato')
        setTab('projetos')
        return
      }

      setTab('chamados')
      setQueueLifecycle('ativos')
      setQueueStatus('')
      setQueueType('')
      setQueueArea('')
      setQueueSearch('')
      setQueueAssignee('')
      setQueueUnassigned(false)

      if (target === 'open_requests') {
        setQueueSla('')
      } else if (target === 'sla_due_soon') {
        setQueueSla('due_soon')
      } else if (target === 'sla_overdue') {
        setQueueSla('overdue')
      }
    },
    [setTab],
  )

  const openRequest = useCallback(
    (id: string) => {
      navigate(financeRequestDetailPath(id))
    },
    [navigate],
  )

  const closeProject = useCallback(() => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev)
      p.delete('project')
      return p
    })
  }, [setSearchParams])

  const load = useCallback(async () => {
    if (tab === 'pagamentos') {
      setLoading(false)
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      if (tab === 'dashboard') {
        const s = await financeService.getSummary()
        setSummary(s)
      } else if (tab === 'chamados') {
        const res = await financeService.listRequests({
          page: 1,
          limit: 100,
          queueLifecycle,
          status: queueStatus || undefined,
          type: queueType || undefined,
          area: queueArea || undefined,
          search: queueSearch.trim() || undefined,
          slaFilter: queueSla || undefined,
          assigneeId: queueAssignee || undefined,
          unassigned: queueUnassigned || undefined,
        })
        setRequests(res.data)
      } else if (tab === 'projetos') {
        const res = await financeService.listProjects({
          page: 1,
          limit: 50,
          projectFilter: projectFilter === 'todos' ? undefined : projectFilter,
        })
        setProjects(res.data)
      } else if (tab === 'pagamentos') {
        // FinancePagamentosSection carrega seus próprios dados
      }
    } catch {
      setError('Não foi possível carregar o módulo financeiro.')
    } finally {
      setLoading(false)
    }
  }, [tab, projectFilter, queueLifecycle, queueStatus, queueType, queueArea, queueSearch, queueSla, queueAssignee, queueUnassigned])

  useEffect(() => {
    if (searchParams.get('tab') === 'fila') {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev)
        p.set('tab', 'chamados')
        return p
      })
    }
  }, [searchParams, setSearchParams])

  useEffect(() => {
    if (!canManageQueue) return
    void financeService.listAssignees().then(setAssignees).catch(() => setAssignees([]))
    void financeService.listTypes().then((types) => setTypeOptions(types.map((t) => ({ key: t.key, label: t.label }))))
  }, [canManageQueue])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (legacyRequestId) {
      navigate(financeRequestDetailPath(legacyRequestId), { replace: true })
    }
  }, [legacyRequestId, navigate])

  useEffect(() => {
    if (!selectedProjectId) return
    void financeService
      .getProject(selectedProjectId)
      .then((project) => {
        navigate(
          `/clientes/${project.empresa.id}?tab=projetos&projeto=${project.id}&from=financeiro`,
          { replace: true },
        )
      })
      .catch(() => {
        toast.error('Projeto não encontrado')
        closeProject()
      })
  }, [selectedProjectId, navigate, closeProject])

  const exportQueue = useCallback(async () => {
    try {
      const blob = await financeService.exportRequestsCsv()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `fila-financeiro-${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('Exportação concluída')
    } catch {
      toast.error('Falha ao exportar fila')
    }
  }, [])

  return {
    tab,
    setTab,
    goToStatView,
    summary,
    requests,
    projects,
    loading,
    error,
    reload: load,
    formatFinanceStatus,
    openRequest,
    projectFilter,
    setProjectFilter,
    queueLifecycle,
    setQueueLifecycle,
    queueStatus,
    setQueueStatus,
    queueType,
    setQueueType,
    queueSla,
    setQueueSla,
    queueAssignee,
    setQueueAssignee,
    queueUnassigned,
    setQueueUnassigned,
    queueSearch,
    setQueueSearch,
    queueArea,
    setQueueArea,
    assignees,
    typeOptions,
    canManageQueue,
    exportQueue,
    typeLabelMap: Object.fromEntries(typeOptions.map((t) => [t.key, t.label])),
  }
}
